import { catchError, map, Observable, of } from 'rxjs'
import { Injectable } from '@angular/core'

import { CopyClientDocumentsPreview, CopyClientDocumentsResult, CopyFileSelection } from '@data/interfaces/documents.interface'
import { CopyClientVerificationResult } from '@modules/private/lease/data/interfaces/copy-client-verification.interface'
import { ILease, ILeaseList } from '@modules/private/lease/data/interfaces/ilease.interface'
import { IResponseArray } from '@data/interfaces/iresponseArray.interface'
import { FilterOptions } from '@shared/interfaces/filterOptions.interface'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

/** F-009 · Respuesta de la copia del expediente, con el `code` y el `status` del error cuando falla. */
export interface CopyClientDocumentsResponse<T> {
  error: boolean
  msg: string
  data: T | null
  code?: string
  status?: number
}

@Injectable({
  providedIn: 'root',
})
export class ApiLeaseService extends ApiClass {
  constructor() {
    super()
    this.path = 'leases'
  }

  /**
   * Los arrendamientos ACTIVOS de un cliente, con su tabla de amortización.
   *
   * Lo consume el selector de reparto: cuando un pago va a un cliente con varios contratos vivos, el
   * operador elige a cuáles y en qué orden. Se pide con `populateQuotation` porque la capacidad de cada
   * arrendamiento —cuánto puede absorber— se deriva de sus letras, no de un campo agregado del lease:
   * `periodsOverdue` y `amountToUpdate` los escribe SOLO el devengo nocturno, y con la mora apagada ese
   * proceso sale sin tocar nada, así que pintarlos sería enseñar una cifra congelada.
   */
  getActiveByClient(clientId: string): Observable<ILease[]> {
    this.path = 'leases'
    return this.http.get<{ error: boolean; data: ILease[] }>(`${environment.apiUrl}${this.path}/by-client/${clientId}?onlyActive=true&populateQuotation=true`).pipe(
      map((r) => (r?.error ? [] : r?.data || [])),
      catchError(() => of([] as ILease[])),
    )
  }

  getAll(page = 1, limit = 10, filters = {}) {
    this.path = 'leases'
    const response: ILeaseList = {
      error: true,
      msg: '',
      data: [],
      total: 0,
      status: 200,
      page: 1,
      itemsByPage: 10,
      totalItems: 0,
    }

    const populate = [
      { path: 'client' },
      { path: 'committee' },
      { path: 'documents' },
      {
        path: 'quotation',
        populate: [
          {
            path: 'amortizationPeriods',
            model: 'AmortizationPeriod',
          },
          {
            path: 'assets',
            model: 'Asset',
          },
        ],
      },
    ]

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters, populate }).pipe(
      map((r) => r as ILeaseList),
      catchError(() => of(response)),
    )
  }

  create(entity: any) {
    return this.http.post(environment.apiUrl + 'leases/create', entity)
  }

  delete(id: string) {
    return this.deleteCollection(id).pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  getById(id: string) {
    this.path = 'leases'

    return this.getCollection(id).pipe(map((r) => r as { error: boolean; msg: string; data: ILease }))
  }

  getProviders() {
    return this.http.post(environment.apiUrl + 'providers', { page: 1, limit: 10, filters: {} }).pipe(
      map((r) => r as { error: boolean; msg: string; data: any }),
      catchError(() => of({ error: true, msg: 'Hubo un error de conexión', data: [] })),
    )
  }

  approveLeaseTerm(leaseID: string, quotationId: string, termId: string) {
    return this.putCollection(undefined, undefined, `leases/approve-term/${leaseID}/${quotationId}/${termId}`).pipe(map((r) => r as { error: boolean; msg: string; data: any }))
  }

  fetchAssets(filterOptions: FilterOptions): Observable<{ label: string; value: string; amount: number }[]> {
    this.path = 'assets'

    return this.postCollection(filterOptions).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((asset: { _id: string; name: string; acquisition?: { total: number } }) => ({
            label: asset.name,
            value: asset._id,
            amount: asset.acquisition?.total || 0,
          }))
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al obtener los activos:', error)
        return of([])
      }),
    )
  }

  // Búsqueda server-side de clientes por nombre O RFC (endpoint `clients/search-names`).
  // Reemplaza el viejo preload de 100 clientes filtrado en memoria, que dejaba fuera a los
  // clientes antiguos. Con `q` vacío devuelve los primeros 50 por nombre.
  searchClients(q = ''): Observable<{ label: string; value: string; entity: string; rfc?: string }[]> {
    return this.http.get<any>(environment.apiUrl + 'clients/search-names', { params: { q, limit: 50 } }).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data)) {
          return response.data.map((client: { _id: string; name: string; entity: string; rfc?: string }) => ({
            label: client.name,
            value: client._id,
            entity: client.entity,
            rfc: client.rfc,
          }))
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al buscar los clientes:', error)
        return of([])
      }),
    )
  }

  fetchQuotations(filterOptions: FilterOptions): Observable<{ label: string; value: string }[]> {
    this.path = 'quotation-parameters/find-all'
    // filterOptions.filters = { status: { value: 'proposal', matchMode: 'equals' } }

    return this.postCollection(filterOptions).pipe(
      map((response) => {
        console.log(response)
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((quotation: { _id: string; assetsNames?: string; provitionalAsset?: string; assetsValue?: { total: number }; provitionalValue?: number }) => {
            // `||` a propósito (no `??`): assetsNames viene como '' en QPs con activo provisional
            const name = quotation.assetsNames || quotation.provitionalAsset || 'Activo por definir'
            const total = quotation.assetsValue?.total ?? quotation.provitionalValue ?? 0
            return {
              label: `${name} - ${total.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}`,
              value: quotation._id,
            }
          })
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg)
          return []
        }
      }),
      catchError((error) => {
        console.error('Error al obtener las cotizaciones:', error)
        return of([])
      }),
    )
  }

  /**
   * POST /leases/:id/verify-ciec — corre la CIEC guardada y vuelve a tomar el snapshot del análisis.
   *
   * ! No usa `postCollection`: su `catchError` descarta el `code` del error, y desde B-075 un análisis
   * ! fallido responde 400 con `code` (`CIEC_REJECTED`, `SYNTAGE_UNAVAILABLE`, …) en vez de re-snapshotear
   * ! el anterior. Sin el código, el expediente solo podía decir «no se pudo»; con él dice qué pasó y qué sigue.
   */
  runCiecVerification(leaseId: string): Observable<{ error: boolean; msg: string; data: any; code?: string }> {
    return this.http.post<{ error: boolean; msg: string; data: any }>(`${environment.apiUrl}leases/${leaseId}/verify-ciec`, {}).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: r?.data ?? null })),
      catchError((e) => of({ error: true, msg: e?.error?.msg ?? e?.error?.message ?? 'No se pudo ejecutar la verificación CIEC', data: null, code: e?.error?.code })),
    )
  }

  /**
   * B-004 · POST /leases/:id/copy-client-verification — copia al arrendamiento el análisis vigente del cliente cuando el
   * suyo vino vacío o no existe. Con `dryRun` solo dice si se puede y qué llegaría. Como `runCiecVerification`, no usa
   * `postCollection`: el 409 trae un `code` (`LEASE_HAS_ANALYSIS`, `NO_CLIENT_ANALYSIS`…) que la pantalla necesita.
   */
  copyClientVerification(leaseId: string, dryRun: boolean): Observable<{ error: boolean; msg: string; data: CopyClientVerificationResult | null; code?: string }> {
    return this.http
      .post<{ error: boolean; msg: string; data: CopyClientVerificationResult }>(`${environment.apiUrl}leases/${leaseId}/copy-client-verification${dryRun ? '?dryRun=true' : ''}`, {})
      .pipe(
        map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: r?.data ?? null })),
        catchError((e) => of({ error: true, msg: e?.error?.msg ?? e?.error?.message ?? 'No se pudo copiar el análisis del cliente', data: null, code: e?.error?.code })),
      )
  }

  /**
   * POST /leases/:id/import-ekatena — trae el análisis que ya existe en Ekatena.
   *
   * Importa a nivel cliente y vuelve a tomar el snapshot, igual que verificar. Aditivo y sin costo:
   * el reporte ya lo pagó quien lo corrió en el panel del proveedor.
   */
  importEkatena(leaseId: string) {
    this.path = `leases/${leaseId}/import-ekatena`

    return this.postCollection(undefined).pipe(
      map((response) => response as { error: boolean; msg: string; data: any }),
      catchError((error) => {
        //* El backend manda `code` + `message` cuando no hay nada que importar o el reporte va a
        //* medias. Se propaga tal cual: dice mejor lo que pasó que un genérico del front.
        return of({ error: true, msg: error?.error?.message ?? 'No se pudo importar el análisis de Ekatena', data: null })
      }),
    )
  }

  /**
   * ADR-0021 — Descargar el Excel original del análisis de riesgo (proxy con auth).
   * El blob es privado, así que pedimos el archivo al backend que lo bajará
   * desde Azure y lo streamea con headers correctos.
   */
  downloadRiskExcel(leaseId: string) {
    return this.http.get(`${environment.apiUrl}risk/excel/${leaseId}/download`, {
      responseType: 'blob',
      observe: 'response',
    })
  }

  /**
   * ADR-0021 — Subir el Excel estandarizado de Análisis de Riesgo.
   * El backend parsea las 5 hojas y sobrescribe bankFlows/ratios/calculator del lease.
   */
  importRiskExcel(leaseId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return this.http.post<any>(`${environment.apiUrl}risk/import-excel/${leaseId}`, formData).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Importación exitosa', data: r?.data ?? null })),
      catchError((e) => {
        const msg = e?.error?.msg || e?.error?.message || 'Error al importar el Excel'
        console.error('Error al importar Excel de riesgo:', e)
        return of({ error: true, msg, data: e?.error?.data ?? null })
      }),
    )
  }

  /**
   * ADR-0021 — Reemplazar el Excel del análisis de riesgo. Endpoint paralelo a
   * `importRiskExcel`, restringido a roles con permiso `risk.re-upload` (Admin
   * y Finanzas). El backend borra los docs y el .xlsx anteriores tras guardar
   * los nuevos (best-effort, ver `applyParsedRisk`).
   */
  replaceRiskExcel(leaseId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return this.http.put<any>(`${environment.apiUrl}risk/import-excel/${leaseId}`, formData).pipe(
      map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? 'Reemplazo exitoso', data: r?.data ?? null })),
      catchError((e) => {
        const msg = e?.error?.msg || e?.error?.message || 'Error al reemplazar el Excel'
        console.error('Error al reemplazar Excel de riesgo:', e)
        return of({ error: true, msg, data: e?.error?.data ?? null })
      }),
    )
  }

  // ========== MÉTODOS PARA GESTIÓN DE DOCUMENTOS ==========

  /**
   * Descargar documento por URL
   */
  downloadDocumentByUrl(fileUrl: string): Observable<Blob> {
    const encodedUrl = encodeURIComponent(fileUrl)
    return this.http
      .get(`${environment.apiUrl}documents/download?fileUrl=${encodedUrl}`, {
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          console.error('Error al descargar archivo por URL:', error)
          throw error
        }),
      )
  }

  /**
   * Registrar el primer pago del arrendamiento
   */
  registerFirstPayment(leaseId: string, paymentData: any): Observable<{ error: boolean; msg: string; data: any }> {
    return this.http.post(`${environment.apiUrl}leases/${leaseId}/register-first-payment`, paymentData).pipe(
      map((response) => response as { error: boolean; msg: string; data: any }),
      catchError((error) => {
        //* El msg del backend es el único que dice QUÉ falló (cotización ya asignada, comprobante
        //* faltante, fecha inválida). Sustituirlo por un genérico dejaba al comercial sin nada que leer.
        const msg = error?.error?.msg || 'Error al registrar el primer pago'
        console.error('Error al registrar el primer pago:', error)
        return of({ error: true, msg, data: null })
      }),
    )
  }

  /**
   * Recorrer la fecha de inicio (período 1) de la tabla de amortización. El backend
   * recalcula en cascada el resto de fechas preservando la cadencia mensual, sincroniza
   * startDate/endDate del lease y registra el cambio en la auditoría. Solo modifica fechas.
   * Requiere permiso leases.update (Admin / Operaciones).
   */
  editAmortizationStartDate(leaseId: string, startDate: string, reason: string, confirmPaidPeriods = false): Observable<{ error: boolean; msg: string; data: ILease }> {
    return this.http.patch(`${environment.apiUrl}leases/${leaseId}/amortization/start-date`, { startDate, reason, confirmPaidPeriods }).pipe(
      map((response) => response as { error: boolean; msg: string; data: ILease }),
      catchError((error) => {
        const msg = error?.error?.msg || 'Error al actualizar la fecha de inicio de la tabla de amortización'
        console.error('Error al editar la fecha de inicio de amortización:', error)
        return of({ error: true, msg, data: null as any })
      }),
    )
  }

  /**
   * Edita los montos del arrendamiento. Con `dryRun` el backend calcula todo, arma el diff y
   * aborta la transacción: la previsualización corre exactamente el mismo código que la
   * aplicación, así que no puede desincronizarse de ella.
   *
   * Requiere el permiso `leases.edit-amounts` (Admin / Operaciones).
   */
  editLeaseAmounts(leaseId: string, payload: any, dryRun: boolean): Observable<{ error: boolean; msg: string; data: any }> {
    return this.http.post(`${environment.apiUrl}leases/${leaseId}/amounts?dryRun=${dryRun}`, payload).pipe(
      map((response) => response as { error: boolean; msg: string; data: any }),
      catchError((error) => {
        const msg = error?.error?.msg || 'Error al procesar la edición de montos del arrendamiento'
        console.error('Error al editar montos del arrendamiento:', error)
        return of({ error: true, msg, data: null as any })
      }),
    )
  }

  /** Asigna (o desasigna con walletId=null) la cartera de un arrendamiento. Requiere leases.update. */
  assignWallet(leaseId: string, walletId: string | null): Observable<{ error: boolean; msg: string; data: ILease }> {
    return this.http.patch(`${environment.apiUrl}leases/${leaseId}/wallet`, { walletId }).pipe(
      map((response) => response as { error: boolean; msg: string; data: ILease }),
      catchError((error) => {
        const msg = error?.error?.msg || 'Error al asignar la cartera al arrendamiento'
        console.error('Error al asignar cartera:', error)
        return of({ error: true, msg, data: null as any })
      }),
    )
  }

  /** Asigna el activo REAL a un lease originado con activo provisional (asset=null). Requiere leases.update. */
  assignAsset(leaseId: string, assetId: string): Observable<{ error: boolean; msg: string; data: ILease }> {
    return this.http.patch(`${environment.apiUrl}leases/${leaseId}/assign-asset`, { assetId }).pipe(
      map((response) => response as { error: boolean; msg: string; data: ILease }),
      catchError((error) => {
        const msg = error?.error?.msg || 'Error al asignar el activo al arrendamiento'
        console.error('Error al asignar activo:', error)
        return of({ error: true, msg, data: null as any })
      }),
    )
  }

  /** Opciones de cartera (id + nombre) para el selector de asignación. Gated por leases.get-all. */
  getWalletOptions(): Observable<{ _id: string; name: string }[]> {
    return this.http.get<{ data: { _id: string; name: string }[] }>(`${environment.apiUrl}leases/wallet-options`).pipe(
      map((response) => response.data || []),
      catchError(() => of([])),
    )
  }

  /**
   * F-009 · POST /leases/:id/copy-client-documents — copia al arrendamiento los archivos aprobados que se marcaron del
   * expediente del cliente. Con `dryRun` lista qué se puede marcar y si se puede copiar, sin escribir. La copia ya no
   * devuelve el arrendamiento: hay que recargarlo. Como `copyClientVerification`, no usa `postCollection`: el 409 trae
   * `code: 'LEASE_CLOSED'` y la pantalla necesita el `status` para no enseñar un 500 en inglés.
   */
  copyClientDocuments(leaseId: string, opts: { dryRun: true }): Observable<CopyClientDocumentsResponse<CopyClientDocumentsPreview>>
  copyClientDocuments(leaseId: string, opts: { files: CopyFileSelection[] }): Observable<CopyClientDocumentsResponse<CopyClientDocumentsResult>>
  copyClientDocuments(leaseId: string, opts: { dryRun: true } | { files: CopyFileSelection[] }): Observable<CopyClientDocumentsResponse<CopyClientDocumentsPreview | CopyClientDocumentsResult>> {
    const dryRun = 'dryRun' in opts
    const body = 'files' in opts ? { files: opts.files } : {}
    return this.http
      .post<{ error: boolean; msg: string; data: CopyClientDocumentsPreview | CopyClientDocumentsResult }>(
        `${environment.apiUrl}leases/${leaseId}/copy-client-documents${dryRun ? '?dryRun=true' : ''}`,
        body,
      )
      .pipe(
        map((r) => ({ error: r?.error ?? false, msg: r?.msg ?? '', data: r?.data ?? null })),
        catchError((e) => of({ error: true, msg: e?.error?.msg ?? e?.error?.message ?? '', data: null, code: e?.error?.code, status: e?.status })),
      )
  }

  // ========== MÉTODOS PARA SISTEMA DE COMENTARIOS ==========

  /**
   * Crear un nuevo comentario en un lease
   */
  createComment(leaseId: string, content: string): Observable<{ error: boolean; msg: string; data: ILease }> {
    const commentData = {
      content: content,
    }

    return this.http.post(`${environment.apiUrl}leases/${leaseId}/comments`, commentData).pipe(
      map((response) => response as { error: boolean; msg: string; data: ILease }),
      catchError((error) => {
        console.error('Error al crear comentario:', error)
        return of({ error: true, msg: 'Error al crear comentario', data: null as any })
      }),
    )
  }

  /**
   * Obtener todos los comentarios de un lease
   */
  getComments(leaseId: string): Observable<{ error: boolean; msg: string; data: any[] }> {
    return this.http.get(`${environment.apiUrl}leases/${leaseId}/comments`).pipe(
      map((response) => response as { error: boolean; msg: string; data: any[] }),
      catchError((error) => {
        console.error('Error al obtener comentarios:', error)
        return of({ error: true, msg: 'Error al obtener comentarios', data: [] })
      }),
    )
  }

  /**
   * Eliminar un comentario específico de un lease
   */
  deleteComment(leaseId: string, commentId: string): Observable<{ error: boolean; msg: string; data: ILease }> {
    return this.http.delete(`${environment.apiUrl}leases/${leaseId}/comments/${commentId}`).pipe(
      map((response) => response as { error: boolean; msg: string; data: ILease }),
      catchError((error) => {
        console.error('Error al eliminar comentario:', error)
        return of({ error: true, msg: 'Error al eliminar comentario', data: null as any })
      }),
    )
  }

  /**
   * Tasa moratoria mensual del arrendamiento (F24). `rate` va en decimal (0.035 = 3.5 %) entre 0 y 0.20
   * y el motivo es obligatorio: queda en la Bitácora como MORATORY_RATE_CHANGED. Requiere
   * `leases.edit-amounts`, el mismo permiso que el editor de saldos, porque mueve dinero futuro.
   */
  patchMoratoryRate(
    leaseId: string,
    payload: { rate: number; reason: string },
  ): Observable<{ error: boolean; msg: string; data: { moratoryRate: number; previous: number; auditEntryId: string } | null }> {
    return this.http.patch(`${environment.apiUrl}leases/${leaseId}/moratory-rate`, payload).pipe(
      map((response) => response as { error: boolean; msg: string; data: { moratoryRate: number; previous: number; auditEntryId: string } }),
      catchError((error) => {
        const msg = error?.error?.msg || 'Error al actualizar la tasa moratoria del arrendamiento'
        return of({ error: true, msg, data: null })
      }),
    )
  }
}
